import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["lookupResult", "lookupQuery", "lookupButton"];

  connect() {
    // Initialize Semantic UI tabs. Same selector the invite form's inline
    // script used before this controller was registered — scoped to
    // [data-tab] so it can't catch unrelated .menu .item elements.
    window.$(this.element).find(".tabular.menu .item[data-tab]").tab();

    this.initValidation();
  }

  // Fomantic form validation for the two forms in this panel. This lived in a
  // `:javascript` block in _invite_form.html.haml; it belongs here, with the
  // rest of this panel's behaviour, and scoped to the controller element
  // rather than reaching across the document by id.
  initValidation() {
    const $ = window.$;

    // keyboardShortcuts MUST stay off. Fomantic's Enter handler submits via
    // the native HTMLFormElement.submit(), which does not fire a 'submit'
    // event — so Turbo never intercepts it, the browser navigates, and
    // lookup_influencer answers the HTML format with a redirect back to this
    // page. The lookup did nothing at all for anyone who pressed Enter rather
    // than clicking "Look up". With it off, the browser's own implicit
    // submission fires a real submit event: validation still runs, and Turbo
    // streams the result card in exactly as it does for a click.
    $(this.element)
      .find("form#lookupInfluencer")
      .form({
        inline: true,
        on: "submit",
        keyboardShortcuts: false,
        fields: {
          query: {
            identifier: "query",
            rules: [
              { type: "empty", prompt: "Enter a handle, email, or name" },
            ],
          },
        },
      });

    $(this.element)
      .find("form#bulkInviteForm")
      .form({
        inline: true,
        on: "submit",
        fields: {
          emails: {
            identifier: "emails",
            rules: [
              {
                type: "empty",
                prompt: "Please enter at least one email address",
              },
            ],
          },
        },
      });

    // application.js's clipboard handler calls window[data-clipboard-callback],
    // so this name has to exist globally or copying throws. It is a no-op —
    // defined here only so the template carries no script of its own.
    window.enableCta = window.enableCta || function () {};
  }

  // The lookup is a live call out to Meta and lands around 2-3s. With a static
  // button and an empty result area that reads as a dead form — people hit
  // "Look up" again rather than wait. Show the work instead: spin the button
  // and stand a placeholder card in the exact spot the real one will occupy,
  // so the result swaps in rather than appearing from nowhere.
  startLookup() {
    if (this.hasLookupButtonTarget) {
      this.lookupButtonTarget.classList.add("loading", "disabled");
    }
    if (!this.hasLookupResultTarget) return;

    const query = this.hasLookupQueryTarget
      ? this.lookupQueryTarget.value.trim()
      : "";

    const card = document.createElement("div");
    card.className = "add-influencer-card";

    const loader = document.createElement("div");
    loader.className = "ui active inline loader";
    card.appendChild(loader);

    const text = document.createElement("div");
    const name = document.createElement("div");
    name.className = "add-influencer-card__name";
    // textContent, not innerHTML — this is whatever the brand typed.
    name.textContent = query ? `Looking up ${query}…` : "Looking up…";
    const sub = document.createElement("div");
    sub.className = "add-influencer-card__handle";
    sub.textContent = "Fetching their public profile and recent posts";
    text.appendChild(name);
    text.appendChild(sub);
    card.appendChild(text);

    this.lookupResultTarget.replaceChildren(card);
  }

  // Once a result is showing, its Add is the next step: Look up steps down to
  // a bordered button so the result's action is the one coral one.
  endLookup() {
    if (this.hasLookupButtonTarget) {
      this.lookupButtonTarget.classList.remove("loading", "disabled");
      this.setLookupEmphasis(!this.hasResult());
    }
  }

  hasResult() {
    return (
      this.hasLookupResultTarget && this.lookupResultTarget.children.length > 0
    );
  }

  setLookupEmphasis(primary) {
    if (!this.hasLookupButtonTarget) return;
    this.lookupButtonTarget.classList.toggle("primary", primary);
    this.lookupButtonTarget.classList.toggle("basic", !primary);
  }

  // "Not the right person" on the vetting card: clear the streamed result and
  // put the cursor back in the single input, rather than leaving the wrong
  // creator on screen next to an Add button.
  clearLookup(event) {
    event.preventDefault();
    if (this.hasLookupResultTarget) this.lookupResultTarget.innerHTML = "";
    this.setLookupEmphasis(true);
    if (this.hasLookupQueryTarget) {
      this.lookupQueryTarget.value = "";
      this.lookupQueryTarget.focus();
    }
  }
}
